/** Shared vision-card visuals used by /app/vision and the Dashboard widget. */

// Days between today and a YYYY-MM-DD target. Returns { days, phase, label }.
export function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(`${dateStr}T23:59:59`);
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (days > 30) return { days, phase: "far", label: `${days} days left` };
  if (days > 7)  return { days, phase: "near", label: `${days} days left` };
  if (days > 1)  return { days, phase: "close", label: `${days} days left` };
  if (days === 1) return { days, phase: "close", label: "Tomorrow!" };
  if (days === 0) return { days, phase: "today", label: "Today is the day" };
  return { days, phase: "past", label: `${Math.abs(days)} days past` };
}

// A curated palette of gradients + hero background photos (from unsplash-images
// mirror). Deterministically chosen by category/hash so a given goal always
// looks the same. All images are free-to-use.
const GRADIENTS = [
  { from: "#F97316", to: "#DC2626" }, // sunrise → crimson
  { from: "#8B5CF6", to: "#EC4899" }, // violet → pink
  { from: "#0EA5E9", to: "#22D3EE" }, // sky → cyan
  { from: "#10B981", to: "#06B6D4" }, // emerald → cyan
  { from: "#F59E0B", to: "#EF4444" }, // amber → red
  { from: "#6366F1", to: "#8B5CF6" }, // indigo → violet
  { from: "#059669", to: "#65A30D" }, // green → lime
  { from: "#7C3AED", to: "#2563EB" }, // purple → blue
];

const CATEGORY_IMAGES = {
  business: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80",
  career: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=1200&q=80",
  finances: "https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?w=1200&q=80",
  fitness: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&q=80",
  health: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1200&q=80",
  travel: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80",
  education: "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=1200&q=80",
  ai_skills: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&q=80",
  personal_growth: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80",
  relationships: "https://images.unsplash.com/photo-1518199266791-5375a83190b7?w=1200&q=80",
  parenting: "https://images.unsplash.com/photo-1502086223501-7ea6ecd79368?w=1200&q=80",
  reading: "https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=1200&q=80",
  mountains: "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1200&q=80",
  ocean: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80",
};

function hashStr(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Choose a visual (image URL + fallback gradient) for a vision goal. */
export function visualFor(item) {
  const key = (item.category || item.content || item.id || "").toLowerCase();
  const g = GRADIENTS[hashStr(key) % GRADIENTS.length];
  // Prefer user-supplied image; else pick a category image; else gradient only.
  let img = item.image_url || null;
  // Absolutize relative /api/... URLs (uploaded via /api/vision/upload).
  if (img && img.startsWith("/api/")) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    img = `${base}${img}`;
  }
  if (!img) {
    for (const [k, v] of Object.entries(CATEGORY_IMAGES)) {
      if (key.includes(k)) { img = v; break; }
    }
  }
  return { image: img, gradient: g };
}

export const COUNTDOWN_PHASE_STYLES = {
  today: { pill: "bg-[#DC2626] text-white", ring: "ring-[#DC2626]" },
  close: { pill: "bg-[#F97316] text-white", ring: "ring-[#F97316]" },
  near:  { pill: "bg-[#B45309] text-white", ring: "ring-[#B45309]" },
  far:   { pill: "bg-[#059669] text-white", ring: "ring-[#059669]" },
  past:  { pill: "bg-[#4B5563] text-white", ring: "ring-[#4B5563]" },
};
